import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FaTable } from "react-icons/fa6";
import axios from "axios";
import Sidebar from "../Sidebar";
import * as XLSX from "xlsx";
import TopBar from "../TopBar";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

const PdfButton = styled.button`
  background-color: #9848c7;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background-color: #7d38a4;
  }
`;

const ScrollContainer = styled.div`
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  font-size: 0.7rem;

  th,
  td {
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 240px;
  }

  th {
    background-color: #9848c7;
    font-weight: 600;
    color: #333;
    cursor: pointer;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #eef6ff;
  }

  td.positive {
    color: green;
    font-weight: bold;
  }

  td.negative {
    color: red;
    font-weight: bold;
  }

  td.bold {
    font-weight: bold;
  }
`;

const PaginationControls = styled.div`
  margin-top: 10px;
  padding: 10px 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;

  button {
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #9848c7;
    background: #9848c7;
    color: #fff;
    border-radius: 4px;
    transition: background 0.2s ease, transform 0.1s ease;

    &:hover:not(:disabled) {
      background-color: #62b2d6;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  span {
    align-self: center;
    font-size: 14px;
    font-weight: bold;
    color: #333;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const ExportButton = styled.button`
  background-color: #2e7d32;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background-color: #1b5e20;
  }
`;

const ResetButton = styled.button`
  background-color: #d32f2f;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #b71c1c;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  align-items: flex-end;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 160px;
`;

const FilterLabel = styled.label`
  font-size: 0.75rem;
  font-weight: bold;
  margin-bottom: 4px;
  color: #333;
`;

const FilterInput = styled.input`
  padding: 6px 10px;
  font-size: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ZakupyData = () => {
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState({
    NrDostawcy: "",
    NazwaDostawcy: "",
    DataZakupu: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState(null);
  const recordsPerPage = 20;

  const hiddenColumns = [
    "id",
    "kodSystemowy",
    "wersjaSchemy",
    "WariantFormularza",
    "DataWytworzeniaJPK",
    "NazwaSystemu",
    "CelZlozenia",
    "poz",
    "KodUrzedu",
    "rola",
    "Email",
    "DataWplywu",
    "DokumentZakupu",
    "KodFormularza",
    // NEW: hide NIP + Nazwa (PelnaNazwa) from Zakupy Dane
    "NIP",
    "PelnaNazwa",
  ];

  const formatCurrency = (value) => {
    if (!value) return "-";
    const num = parseFloat(value.toString().replace(/[^\d.-]/g, ""));
    if (isNaN(num)) return "-";
    return num.toLocaleString("pl-PL");
  };

  // NEW boolean columns ST + IMP added
  const newColumns = [
    "Amount",
    "Vat Amount",
    "Domestic Transaction",
    "EU transaction",
    "ST",
    "IMP",
  ];

  const computeAdditionalColumns = (row) => {
    const additionalColumns = {
      Amount: "-",
      "Vat Amount": "-",
      "Domestic Transaction": "✗",
      "EU transaction": "✗",
      ST: "✗",
      IMP: "✗",
    };

    // Amount (K_42) → Domestic vs EU
    if (row.K_42) {
      additionalColumns.Amount = `${row.K_42} PLN`;
      if (row.KodKrajuNadaniaTIN && row.KodKrajuNadaniaTIN !== "PL") {
        additionalColumns["EU transaction"] = "✓";
      } else {
        additionalColumns["Domestic Transaction"] = "✓";
      }
    }

    // Vat Amount (K_43)
    if (row.K_43) {
      additionalColumns["Vat Amount"] = `${row.K_43} PLN`;
      if (row.KodKrajuNadaniaTIN && row.KodKrajuNadaniaTIN !== "PL") {
        additionalColumns["EU transaction"] = "✓";
      } else {
        additionalColumns["Domestic Transaction"] = "✓";
      }
    }

    // NEW IF: if K_40 then ST = true
    if (row.K_40) {
      additionalColumns.ST = "✓";
    }

    // NEW IF: If IMP or IMP_EE or IMP_MM then IMP = true
    const hasImp =
      row.IMP === "1" ||
      row.IMP === 1 ||
      row.IMP_EE === "1" ||
      row.IMP_EE === 1 ||
      row.IMP_MM === "1" ||
      row.IMP_MM === 1;
    if (hasImp) {
      additionalColumns.IMP = "✓";
    }

    return additionalColumns;
  };

  const columnLabels = {
    Amount: "Kwota",
    "Vat Amount": "VAT",
    LpZakupu: "LP",
    KodKrajuNadaniaTIN: "Kraj",
    "Domestic Transaction": "Domestic",
    "EU transaction": "EU",
    ST: "ST",
    IMP: "IMP",
    KodFormularza: "Kod",
    NrDostawcy: "Nr Dostawcy",
    NazwaDostawcy: "Nazwa Dostawcy",
    DowodZakupu: "Dowod Zakupu",
    DataZakupu: "Data Zakupu",
  };

  const fetchData = async () => {
    try {
      console.log("Fetching zakupy data...");
      const response = await axios.get(
        "https://server229256.nazwa.pl/api/zakupy",
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      const fetchedData = response.data.map((row) => ({
        ...row,
        ...computeAdditionalColumns(row),
      }));

      if (fetchedData.length > 0) {
        let columnNames = Object.keys(fetchedData[0]).filter(
          (column) =>
            !hiddenColumns.includes(column) && !column.startsWith("K_")
        );

        // insert dynamic columns
        newColumns.forEach((newColumn) => {
          if (!columnNames.includes(newColumn)) {
            const insertIndex =
              columnNames.indexOf("DataWystawienia") + 1 ||
              columnNames.length;
            columnNames.splice(insertIndex, 0, newColumn);
          }
        });

        setColumns(columnNames);
      }

      setData(fetchedData);
      console.log("Zakupy data fetched and processed successfully.");
    } catch (error) {
      console.error("Error fetching zakupy data:", error);
      setMessage({
        text: `Error fetching zakupy data: ${error.message}`,
        isError: true,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: value,
    }));
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    return (
      (filters.NrDostawcy === "" ||
        (item.NrDostawcy && item.NrDostawcy.includes(filters.NrDostawcy))) &&
      (filters.NazwaDostawcy === "" ||
        (item.NazwaDostawcy &&
          item.NazwaDostawcy.includes(filters.NazwaDostawcy))) &&
      (filters.DataZakupu === "" ||
        (item.DataZakupu && item.DataZakupu.includes(filters.DataZakupu)))
    );
  });

  const totalPages = Math.ceil(filteredData.length / recordsPerPage);
  const currentRecords = filteredData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatValue = (column, value) => {
    if (["NIP", "NrDostawcy"].includes(column)) {
      return value ? String(value).split(".")[0] : "-";
    }
    return value || "-";
  };

  const handleExportToExcel = () => {
    const exportData = data.map((row) =>
      columns.reduce((acc, column) => {
        let value = row[column];

        if (column === "Amount" || column === "Vat Amount") {
          value = value ? parseFloat(value.replace(/[^\d.-]/g, "")) : 0;
          value = isNaN(value) ? 0 : value.toLocaleString("pl-PL");
        }

        acc[columnLabels[column] || column] =
          value !== undefined && value !== null ? value : "";
        return acc;
      }, {})
    );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Zakupy Data");
    XLSX.writeFile(workbook, "ZakupyData.xlsx");
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });

    const sortedData = [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (["Amount", "Vat Amount"].includes(key)) {
        aValue = parseFloat(
          (aValue || "").toString().replace(/[^\d.-]/g, "").replace(",", ".")
        );
        bValue = parseFloat(
          (bValue || "").toString().replace(/[^\d.-]/g, "").replace(",", ".")
        );
        if (isNaN(aValue)) aValue = 0;
        if (isNaN(bValue)) bValue = 0;
        return direction === "ascending" ? aValue - bValue : bValue - aValue;
      }

      aValue = (aValue ?? "").toString();
      bValue = (bValue ?? "").toString();

      return direction === "ascending"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    setData(sortedData);
  };

  const getArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? " ▲" : " ▼";
    }
    return "";
  };

  const getSortedFilteredData = () => {
    const rows = [...filteredData];
    if (!sortConfig.key) return rows;

    const { key, direction } = sortConfig;
    return rows.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (["Amount", "Vat Amount"].includes(key)) {
        aValue = parseFloat(
          (aValue || "").toString().replace(/[^\d.-]/g, "").replace(",", ".")
        );
        bValue = parseFloat(
          (bValue || "").toString().replace(/[^\d.-]/g, "").replace(",", ".")
        );
        if (isNaN(aValue)) aValue = 0;
        if (isNaN(bValue)) bValue = 0;
        return direction === "ascending" ? aValue - bValue : bValue - aValue;
      }

      aValue = (aValue ?? "").toString();
      bValue = (bValue ?? "").toString();
      return direction === "ascending"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  const formatForPdf = (column, value) => {
    if (["Amount", "Vat Amount"].includes(column)) {
      return formatCurrency(value);
    }
    if (["NIP", "NrDostawcy"].includes(column)) {
      return value ? String(value).split(".")[0] : "-";
    }
    return value ?? "-";
  };

  const getUserEmail = () => {
    const emailRe = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
    const preferredKeys = ["user", "profile", "auth", "account", "email", "Email"];

    const extractEmail = (raw) => {
      if (!raw) return "";
      try {
        const o = JSON.parse(raw);
        const guess =
          o?.Email ||
          o?.email ||
          o?.user?.Email ||
          o?.user?.email ||
          o?.profile?.Email ||
          o?.profile?.email ||
          o?.account?.Email ||
          o?.account?.email ||
          "";
        if (emailRe.test(String(guess))) return String(guess).trim();
        for (const k of Object.keys(o)) {
          const v = o[k];
          if (typeof v === "string" && emailRe.test(v)) return v.match(emailRe)[0];
          if (v && typeof v === "object") {
            for (const kk of Object.keys(v)) {
              const vv = v[kk];
              if (typeof vv === "string" && emailRe.test(vv))
                return vv.match(emailRe)[0];
            }
          }
        }
      } catch {
        const m = raw.match(emailRe);
        if (m) return m[0];
      }
      return "";
    };

    for (const key of preferredKeys) {
      const raw = localStorage.getItem(key);
      const em = extractEmail(raw);
      if (em) return em;
    }
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      const raw = localStorage.getItem(k);
      const em = extractEmail(raw);
      if (em) return em;
    }
    return "-";
  };

  const getLocalStamp = () =>
    new Date().toLocaleString("pl-PL", {
      timeZone: "Europe/Warsaw",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

  const findColIndex = (cols, name) =>
    cols.findIndex(
      (c) => String(c).toLowerCase() === String(name).toLowerCase()
    );

const handleExportToPDF = () => {
  try {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const left = 12,
      right = 12,
      top = 16,
      bottom = 14;
    const pageWidth = doc.internal.pageSize.getWidth();

    const email = getUserEmail();
    const dateStr = getLocalStamp();

    // header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(30, 30, 30);
    doc.text("Zakupy Dane", left, top);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(70, 70, 70);
    doc.text(email || "-", pageWidth - right, top, { align: "right" });
    doc.text(dateStr, pageWidth - right, top + 5, { align: "right" });

    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    const filterBits = [];
    if (filters.NrDostawcy) filterBits.push(`NrDostawcy: ${filters.NrDostawcy}`);
    if (filters.NazwaDostawcy)
      filterBits.push(`Nazwa: ${filters.NazwaDostawcy}`);
    if (filters.DataZakupu) filterBits.push(`Data: ${filters.DataZakupu}`);
    const filterLine = filterBits.length
      ? `Filtry: ${filterBits.join("  •  ")}`
      : "Filtry: brak";
    doc.text(filterLine, left, top + 12);

    doc.setDrawColor(210);
    doc.setLineWidth(0.3);
    doc.line(left, top + 15, pageWidth - right, top + 15);

    // ===== TABLE SETUP =====
    const pdfColumns = columns.filter((c) => c !== "LpZakupu"); // no LP
    const head = [pdfColumns.map((c) => columnLabels[c] || c)];

    const sourceRows = getSortedFilteredData();
    const rowsForTable = sourceRows.map((row) =>
      pdfColumns.map((c) => formatForPdf(c, row[c]))
    );

    // columns with icon dots (Domestic, EU, ST, IMP)
    const domesticKey = "Domestic Transaction";
    const euKey = "EU transaction";
    const stKey = "ST";
    const impKey = "IMP";

    const domesticIdx = findColIndex(pdfColumns, domesticKey);
    const euIdx = findColIndex(pdfColumns, euKey);
    const stIdx = findColIndex(pdfColumns, stKey);
    const impIdx = findColIndex(pdfColumns, impKey);

    const colStyles = {};
    pdfColumns.forEach((c, idx) => {
      colStyles[idx] = ["Amount", "Vat Amount"].includes(c)
        ? { halign: "right" }
        : { halign: "center" };
    });

    autoTable(doc, {
      startY: top + 18,
      head,
      body: rowsForTable,
      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 2,
        lineColor: [220, 220, 220],
        lineWidth: 0.2,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: [152, 72, 199], // #9848c7
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: colStyles,
      margin: { left, right, top: top + 4, bottom },

      // Remove text for icon columns (Domestic/EU/ST/IMP)
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const idx = data.column.index;
        if (
          idx === domesticIdx ||
          idx === euIdx ||
          idx === stIdx ||
          idx === impIdx
        ) {
          data.cell.text = [""];
        }
      },

      // Draw colored dots for ✓/✗ in Domestic, EU, ST, IMP
      didDrawCell: (data) => {
        if (data.section !== "body") return;

        const rowIdx = data.row.index;
        const colIdx = data.column.index;

        const rawDomestic = sourceRows[rowIdx]?.[domesticKey];
        const rawEU = sourceRows[rowIdx]?.[euKey];
        const rawST = sourceRows[rowIdx]?.[stKey];
        const rawIMP = sourceRows[rowIdx]?.[impKey];

        const { cell } = data;
        const cx = cell.x + cell.width / 2;
        const cy = cell.y + cell.height / 2;
        const r = 1.4; // fixed radius so dots are always visible

        const drawDot = (isCheck) => {
          if (isCheck) doc.setFillColor(46, 125, 50); // green
          else doc.setFillColor(211, 47, 47); // red
          doc.setDrawColor(255, 255, 255);
          doc.setLineWidth(0);
          if (doc.circle) doc.circle(cx, cy, r, "F");
          else doc.rect(cx - r, cy - r, 2 * r, 2 * r, "F");
        };

        if (colIdx === domesticIdx && rawDomestic) {
          if (rawDomestic === "✓") drawDot(true);
          else if (rawDomestic === "✗") drawDot(false);
        }
        if (colIdx === euIdx && rawEU) {
          if (rawEU === "✓") drawDot(true);
          else if (rawEU === "✗") drawDot(false);
        }
        if (colIdx === stIdx && rawST) {
          if (rawST === "✓") drawDot(true);
          else if (rawST === "✗") drawDot(false);
        }
        if (colIdx === impIdx && rawIMP) {
          if (rawIMP === "✓") drawDot(true);
          else if (rawIMP === "✗") drawDot(false);
        }
      },

      didDrawPage: () => {
        const page = doc.internal.getNumberOfPages();
        const pageStr = `Strona ${page}`;
        doc.setFontSize(9);
        doc.setTextColor(100);
        const y = doc.internal.pageSize.getHeight() - 6;
        doc.text(pageStr, left, y);
      },
    });

    doc.save(`Zakupy_Dane_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (e) {
    console.error("PDF export error:", e);
    alert("Wystąpił błąd podczas generowania PDF.");
  }
};


  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <div style={{ flexShrink: 0 }}>
        <Sidebar />
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TopBar />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 20px 0",
            fontSize: "1.2rem",
            fontWeight: "bold",
            color: "#9848c7",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          <FaTable />
          Zakupy / Dane Źródłowe
        </div>

        <Wrapper>
          <FilterContainer>
            <FilterField>
              <FilterLabel>Nr Dostawcy:</FilterLabel>
              <FilterInput
                type="text"
                value={filters.NrDostawcy}
                onChange={(e) =>
                  handleFilterChange("NrDostawcy", e.target.value)
                }
              />
            </FilterField>

            <FilterField>
              <FilterLabel>Nazwa Dostawcy:</FilterLabel>
              <FilterInput
                type="text"
                value={filters.NazwaDostawcy}
                onChange={(e) =>
                  handleFilterChange("NazwaDostawcy", e.target.value)
                }
              />
            </FilterField>

            <FilterField>
              <FilterLabel>Data Zakupu:</FilterLabel>
              <FilterInput
                type="date"
                value={filters.DataZakupu}
                onChange={(e) =>
                  handleFilterChange("DataZakupu", e.target.value)
                }
              />
            </FilterField>

            <ButtonGroup>
              <ExportButton onClick={handleExportToExcel}>
                <img
                  src="https://img.icons8.com/ios-filled/20/ffffff/ms-excel.png"
                  alt="Excel"
                />
                Eksportuj do Excela
              </ExportButton>

              <PdfButton onClick={handleExportToPDF}>
                <img
                  src="https://img.icons8.com/ios-filled/20/ffffff/pdf-2.png"
                  alt="PDF"
                />
                Eksportuj do PDF
              </PdfButton>

              <ResetButton
                onClick={() => {
                  setFilters({
                    NrDostawcy: "",
                    NazwaDostawcy: "",
                    DataZakupu: "",
                  });
                  setCurrentPage(1);
                }}
              >
                Resetuj Filtry
              </ResetButton>
            </ButtonGroup>
          </FilterContainer>

          <PaginationControls>
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              Pierwsza
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Poprzednia
            </button>
            <span>
              Strona {currentPage} z {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Następna
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Ostatnia
            </button>
          </PaginationControls>

          <ScrollContainer>
            <div style={{ overflowX: "auto" }}>
              <StyledTable>
                <thead>
                  <tr>
                    {columns.map((column, index) => (
                      <th key={index} onClick={() => handleSort(column)}>
                        {columnLabels[column] || column}
                        {getArrow(column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((item, rowIndex) => (
                    <tr key={rowIndex}>
                      {columns.map((column, colIndex) => {
                        const value = item[column];
                        let className = "";
                        if (newColumns.includes(column)) {
                          className =
                            value === "✓"
                              ? "positive"
                              : value === "✗"
                              ? "negative"
                              : "bold";
                        }

                        return (
                          <td key={colIndex} className={className}>
                            {["Amount", "Vat Amount"].includes(column) ? (
                              <strong>{formatCurrency(value)}</strong>
                            ) : newColumns.includes(column) ? (
                              <span className={className}>{value}</span>
                            ) : (
                              formatValue(column, value)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </div>
          </ScrollContainer>
        </Wrapper>
      </div>
    </div>
  );
};

export default ZakupyData;
